import {useCallback, useEffect, useMemo, useState} from 'react';

import {useAvailableUserStatuses} from 'src/hooks/dictionaries';
import {getAccountPage} from 'src/openapi/admin/apis/AccountInfoApi';
import {createUser} from 'src/openapi/admin/apis/CreateUserApi';
import {getGroupName} from 'src/openapi/admin/apis/GetGroupNameApi';
import {getUserDetails} from 'src/openapi/admin/apis/GetUserDetailsApi';
import {getUserIntegrationInfo} from 'src/openapi/admin/apis/GetUserIntegrationInfoApi';
import {updateUserCrmId} from 'src/openapi/admin/apis/UpdateUserCrmIdApi';
import {updateUserDetails} from 'src/openapi/admin/apis/UpdateUserDetailsApi';
import {updateUserIntegrationInfo} from 'src/openapi/admin/apis/UpdateUserIntegrationInfoApi';
import {getLoginMetadata, saveLoginMetadata} from 'src/openapi/admin/apis/UserLoginMetadataApi';
import {UserStatus as UserStatuses} from 'src/openapi/admin/models/UserStatus';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {UserInformation} from 'src/modules/profile/account/create-user/typedef';
import type {Error} from 'src/openapi/admin/models/Error';
import type {UserDetails} from 'src/openapi/admin/models/UserDetails';

const DEFAULT_USER_INFORMATION: UserInformation = {
    email: '',
    firstName: '',
    lastName: '',
    accountId: -1,
    pbId: '',
    sfdcId: '',
    jobTitle: '',
    typeCode: 'T_LC',
    statusCode: 'ENABLED',
    linkedIn: '',
    workPhone: '',
    city: '',
    mobilePhone: '',
    postCode: '',
    address2: '',
    address1: '',
    countryCode: '',
    stateProvince: '',
    group: null,
    templateUser: false,
    expirationDate: '',
    educationAttribute: '',
};

const getDefaultUser = (accountId: number) => ({
    ...DEFAULT_USER_INFORMATION,
    accountId,
});

type Props = {
    userId?: number,
    accountId: number,
};

const toUserDetails = (userInformation: UserInformation): UserDetails => ({
    email: userInformation.email,
    firstName: userInformation.firstName,
    lastName: userInformation.lastName,
    typeCode: userInformation.typeCode,
    statusCode: userInformation.statusCode,
    groupId: userInformation.group?.id,
    jobTitle: userInformation.jobTitle,
    pbId: userInformation.pbId,

    countryCode: userInformation.countryCode,
    stateProvince: userInformation.stateProvince,
    address1: userInformation.address1,
    address2: userInformation.address2,
    city: userInformation.city,
    mobilePhone: userInformation.mobilePhone,
    workPhone: userInformation.workPhone,
    postCode: userInformation.postCode,
    templateUser: userInformation.templateUser,
});

export const useUserInformation = ({userId, accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const [initialUserInformation, setInitialUserInformation] = useState<UserInformation>(getDefaultUser(accountId));
    const [userInformation, setUserInformation] = useState<UserInformation>(getDefaultUser(accountId));
    const [availableStatuses, setAvailableStatuses] = useState<string[] | null>(null);

    const {getAvailableStatuses} = useAvailableUserStatuses();

    const loadNewUserInformation = useCallback(async () => {
        if (accountId) {
            setIsLoading(true);
            const accountResponse = await apiDispatch(getAccountPage({accountId}));
            const loadedAvailableStatuses = await getAvailableStatuses(accountId);
            let countryCode: string | null = null;
            let statusCode: string | null = null;
            let error: Error | null = null;

            if (accountResponse.payload.success && Array.isArray(loadedAvailableStatuses)) {
                countryCode = accountResponse.payload.data.country;
            } else if (accountResponse.payload.error) {
                error = accountResponse.payload.data;
            }

            if (Array.isArray(loadedAvailableStatuses)) {
                statusCode = loadedAvailableStatuses[0];
                setAvailableStatuses(loadedAvailableStatuses);
            } else {
                error = loadedAvailableStatuses;
            }
            setIsLoading(false);
            setLoadingError(error);

            if (countryCode && statusCode) {
                setUserInformation(prevState => ({
                    ...prevState,
                    countryCode: countryCode || '',
                    statusCode: statusCode || '',
                }));
            }
        }
    }, [accountId, apiDispatch, getAvailableStatuses]);

    const loadUserInformation = useCallback(async (id: number) => {
        if (accountId) {
            let error: Error | null = null;

            setIsLoading(true);
            let loadedUserInformation: UserInformation = getDefaultUser(accountId);

            const loadUserInformationResponse = await apiDispatch(getUserDetails({userId: id}));
            if (loadUserInformationResponse.payload.success) {
                const {groupId, ...otherUserInformation} = loadUserInformationResponse.payload.data;
                loadedUserInformation = {...loadedUserInformation, ...otherUserInformation};

                if (groupId) {
                    const loadGroupResponse = await apiDispatch(getGroupName({requestBody: [groupId]}));
                    if (loadGroupResponse.payload.success) {
                        loadedUserInformation.group = {id: groupId, name: loadGroupResponse.payload.data[groupId]};
                    } else {
                        error = loadGroupResponse.payload.data;
                    }
                }
            } else if (loadUserInformationResponse.payload.error) {
                error = loadUserInformationResponse.payload.data;
            }

            const loadedAvailableStatuses = await getAvailableStatuses(accountId);
            if (Array.isArray(loadedAvailableStatuses)) {
                const statusCode = loadedUserInformation.statusCode;
                let filteredAvailableStatuses = loadedAvailableStatuses;
                if (statusCode && UserStatuses.EMPTY !== statusCode) {
                    filteredAvailableStatuses = filteredAvailableStatuses.filter(stat => stat !== UserStatuses.EMPTY);
                }

                if (statusCode && !filteredAvailableStatuses.includes(statusCode)) {
                    filteredAvailableStatuses = [statusCode, ...filteredAvailableStatuses];
                }
                setAvailableStatuses(filteredAvailableStatuses);
            } else {
                error = loadedAvailableStatuses;
            }

            const loadUserIntegrationInfoResponse = await apiDispatch(getUserIntegrationInfo({userId: id}));
            if (loadUserIntegrationInfoResponse.payload.success) {
                loadedUserInformation.sfdcId = loadUserIntegrationInfoResponse.payload.data.crmId || '';
                loadedUserInformation.linkedIn = loadUserIntegrationInfoResponse.payload.data.linkedInUrl || '';
            } else {
                error = loadUserIntegrationInfoResponse.payload.data;
            }

            const loadUserLoginInformationResponse = await apiDispatch(getLoginMetadata({userId: id}));
            if (loadUserLoginInformationResponse.payload.success) {
                const {educationAttribute, expirationDate} = loadUserLoginInformationResponse.payload.data;
                loadedUserInformation.expirationDate = expirationDate || '';
                loadedUserInformation.educationAttribute = educationAttribute || '';
            } else {
                error = loadUserLoginInformationResponse.payload.data;
            }

            setIsLoading(false);

            setInitialUserInformation(loadedUserInformation);
            setUserInformation(loadedUserInformation);
            setLoadingError(error);
        }
    }, [accountId, apiDispatch, getAvailableStatuses]);

    useEffect(() => {
        if (userId) {
            loadUserInformation(userId);
        } else {
            loadNewUserInformation();
        }
    }, [userId, accountId, loadUserInformation, loadNewUserInformation]);

    const processUser = useCallback(async (
        onSuccess: (processedUserId: number, precessUserErrors: string[]) => void | Promise<void>,
        onFailure: (error: string) => void,
        options?: SaveOptions,
    ) => {
        const userDetails = toUserDetails(userInformation);
        let processedUserId = userId;
        if (!userId || !isDeepEqual({prev: toUserDetails(initialUserInformation), current: userDetails})) {
            const response = userId
                ? await apiDispatch(updateUserDetails({
                        userId,
                        userDetailsSaveRequest: {...userDetails, reason: options?.reason},
                    }))
                : await apiDispatch(createUser({createUserRequest: {accountId, ...userDetails}}));
            if (response.payload.error) {
                onFailure(getError({error: response.payload.data, separator: ' '}));
                return;
            }
            processedUserId = response.payload.data;
            setInitialUserInformation(previous => ({
                ...userInformation,
                sfdcId: previous.sfdcId,
                linkedIn: previous.linkedIn,
                expirationDate: previous.expirationDate,
            }));
        }
        if (processedUserId === undefined) {
            return;
        }
        const processUserErrors: string[] = [];

        if (initialUserInformation.sfdcId !== userInformation.sfdcId ||
            initialUserInformation.linkedIn !== userInformation.linkedIn
        ) {
            const updateSFDCIDResponse = await apiDispatch(updateUserIntegrationInfo({
                userId: processedUserId,
                userIntegrationInfoSaveRequest: {
                    crmId: userInformation.sfdcId,
                    linkedInUrl: userInformation.linkedIn,
                    reason: options?.reason,
                },
            }));

            if (updateSFDCIDResponse.payload.error) {
                processUserErrors.push(getError({error: updateSFDCIDResponse.payload.data, separator: ' '}));
            } else {
                setInitialUserInformation(previous => ({
                    ...previous, sfdcId: userInformation.sfdcId, linkedIn: userInformation.linkedIn,
                }));
            }
        }

        if (initialUserInformation.expirationDate !== userInformation.expirationDate) {
            const updateLoginMetadataResponse = await apiDispatch(saveLoginMetadata({
                userId: processedUserId,
                loginMetadataSaveRequest: {expirationDate: userInformation.expirationDate, reason: options?.reason},
            }));

            if (updateLoginMetadataResponse.payload.error) {
                processUserErrors.push(getError({error: updateLoginMetadataResponse.payload.data, separator: ' '}));
            } else {
                setInitialUserInformation(previous => ({...previous, expirationDate: userInformation.expirationDate}));
            }
        }

        await onSuccess(processedUserId, processUserErrors);
    }, [userId, userInformation, initialUserInformation, accountId, apiDispatch]);

    const saveSfdcId = useCallback(async (sfdcId: string, options?: SaveOptions): Promise<string | undefined> => {
        if (!userId) {
            return 'User ID is missing. Reload the page and try again.';
        }

        const response = await apiDispatch(updateUserCrmId({
            userId,
            userCrmIdSaveRequest: {crmId: sfdcId, reason: options?.reason},
        }));

        if (response.payload.error) {
            return getError({error: response.payload.data, separator: ' '});
        }

        setInitialUserInformation(previous => ({...previous, sfdcId}));
        setUserInformation(previous => ({...previous, sfdcId}));
        return undefined;
    }, [userId, apiDispatch]);

    const discardChanges = useCallback(() => {
        if (initialUserInformation) {
            setUserInformation(initialUserInformation);
        }
    }, [initialUserInformation]);

    const isChanged = useMemo(() => {
        if (!userInformation.statusCode) {
            return false;
        }

        return !isDeepEqual({prev: initialUserInformation, current: userInformation});
    }, [userInformation, initialUserInformation]);

    return {
        isLoading,
        loadingError,
        userInformation,
        availableStatuses,
        setUserInformation,
        processUser,
        saveSfdcId,
        discardChanges,
        isChanged,
    };
};
